import styled from "@emotion/styled";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Box, Button, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import ModalWithButtons from "components/molecules/Modal/ModalWithButtons";
import { TenantRole } from "graphqlBase/types";
import { dummyIdPrefix } from "pages/UploadCertificate/config";
import { useGetUserInfo, useHasFileVisibilityPermission, useIsCertificateEditMode } from "lib/userInfo";
import {
  getFileTypeIdForFileTypeName as standardGetFileTypeId,
  uploadCertificateFilesMultiSelectorFamily as standardUploadCertificateFilesMultiSelectorFamily,
} from "pages/UploadCertificate/state/uploadCertificateFileState";
import {
  getFileTypeIdForFileTypeName as gridFormingGetFileTypeId,
  uploadCertificateFilesMultiSelectorFamily as gridFormingUploadCertificateFilesMultiSelectorFamily,
} from "pages/UploadGridFormingCertificate/state/uploadCertificateFileState";
import { UploadCertificateFile } from "pages/UploadCertificate/types";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useRecoilState, useSetRecoilState } from "recoil";
import ordinaryColors from "themes/ordinaryColors";
import translations from "translations";
import { v4 as uuidv4 } from "uuid";
import { toBase64 } from "./FileUtils";
import Props from "./index.d";
import UploadedFileIcon from "./UploadedFileIcon";
import FileVisibilityModal from "./FileVisibilityModal";
export const titleFileStyle = {
  width: "100%",
  color: ordinaryColors.lightTextPrimary,
  fontFeatureSettings: "'clig' off, 'liga' off",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "175% /* 28px */",
  letterSpacing: "0.15px",
};
export const StyledUploadButton = styled(Button)(({ theme }) => ({
  height: "43px",
  width: "100%",
  borderRadius: "0px",
  variant: "outlined",
  border: "1px solid",
  borderColor: ordinaryColors.functionalBlack,
  minWidth: "64px",
  color: ordinaryColors.functionalBlack,
}));

const validFilenameRegex = /^[^\\/:*?"<>|]+$/;

const EditFileNameModal = ({
  onClose,
  onConfirm,
  fileName,
  setFileName,
  testId,
  originalFileName,
}: {
  onClose: () => void;
  fileName?: string;
  setFileName: (value: string) => void;
  onConfirm: () => void;
  testId: string;
  originalFileName: string;
}) => {
  const fileExtensionParts = fileName?.split(".");
  const extension = fileExtensionParts?.pop();
  const fileNameWithoutExtension = (fileExtensionParts ?? []).join(".");

  return (
    <ModalWithButtons
      openModal={!!fileName}
      onClose={onClose}
      title={translations.pages.certificateUpload.renameFile}
      addButtonTitle={translations.globals.button.confirm}
      addButtonClick={fileName !== originalFileName ? onConfirm : onClose}
      width="715px"
      data-testid={testId}
    >
      <TextField
        sx={{
          height: "35px",
          width: "100%",
          padding: "0px",
          marginBottom: "14px",
          borderRadius: "0px",
          "& .MuiFormHelperText-root": {
            marginLeft: "0px",
            fontSize: "12px",
            color: ordinaryColors.error,
          },
          " .MuiOutlinedInput-notchedOutline": {
            borderWidth: "0px",
          },
          "& .MuiOutlinedInput-input": {
            padding: "0.5rem 0.75rem",
            width: "100%",
          },
          "& .MuiOutlinedInput-root": {
            borderRadius: "0px",
            border: "1px solid #9E9E9E66",
            backgroundColor: "transparent",
            padding: "0px",
            "& fieldset": {
              borderRadius: "0px",
            },
          },
        }}
        // label={translations.pages.certificateUpload.renameFile}
        value={fileNameWithoutExtension}
        onChange={(e) => {
          if (validFilenameRegex.test(e.target.value)) {
            setFileName(`${e.target.value}.${extension}`);
          }
        }}
      />
    </ModalWithButtons>
  );
};

const FilesList = ({
  files,
  removeFile,
  setEditFileName,
  setOriginalFileName,
  onVisibilityClick,
  isEdit,
  fileType,
  isPlatformOperator,
}: {
  files: UploadCertificateFile[];
  removeFile: (fileId: string | undefined, dummyCerificateId: string) => () => void;
  setEditFileName: React.Dispatch<React.SetStateAction<UploadCertificateFile | undefined>>;
  setOriginalFileName: React.Dispatch<React.SetStateAction<UploadCertificateFile | undefined>>;
  onVisibilityClick: (file: UploadCertificateFile) => void;
  isEdit?: boolean;
  fileType: string;
  isPlatformOperator?: boolean;
}) => {
  const hasFileVisibilityPermission = useHasFileVisibilityPermission();
  // Check if we're in edit mode from global state - visibility controls should only show during upload
  const isCertificateEditMode = useIsCertificateEditMode();
  const hasVisibilityGrants = (file: UploadCertificateFile) =>
    (file.grantAccessToRoles?.length ?? 0) > 0 || (file.grantAccessToTenantIds?.length ?? 0) > 0;

  const getVisibilityTooltip = (file: UploadCertificateFile) => {
    if (!hasVisibilityGrants(file)) {
      return translations.pages.certificateUpload.fileVisibility.configureAccess;
    }
    const roleCount = file.grantAccessToRoles?.length ?? 0;
    const tenantCount = file.grantAccessToTenantIds?.length ?? 0;
    return `${translations.pages.certificateUpload.fileVisibility.accessGrantedTo} ${roleCount} ${
      roleCount === 1 ? "role" : "roles"
    }, ${tenantCount} ${tenantCount === 1 ? "tenant" : "tenants"}`;
  };

  // Determine if visibility controls should be hidden for a specific file
  // In edit mode, only show visibility for newly added files (files without an id from backend)
  const shouldHideVisibilityForFile = (file: UploadCertificateFile) => {
    // If not in edit mode, show visibility controls
    if (!isEdit && !isCertificateEditMode) return false;
    // In edit mode, hide visibility for existing files (with id), show for new files (without id)
    return !!file.id;
  };

  // Determine if remove button should be shown for a file
  // Only restrict removal for mandatory files (Cover Sheet, Perturbations Test Report)
  // For these: only show remove button for new files (no id) OR if platform operator
  // For all other file types: always allow removal
  const canRemoveFile = (file: UploadCertificateFile) => {
    const isMandatoryFile = fileType === "Cover Sheet" || fileType === "Perturbations Test Report";
    if (!isMandatoryFile) return true;
    return !file.id || isPlatformOperator;
  };

  return (
    <Box sx={{ gap: "0.5rem", display: "flex", flexDirection: "column" }}>
      {files.map((file) => (
        <Box
          key={file.id ?? file.certificateId}
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-around" }}
        >
          <UploadedFileIcon {...file} />
          <Box sx={{ display: "flex", marginLeft: "2rem", width: "100%" }}>
            <Typography sx={titleFileStyle}>{file.certificateFileName}</Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: "-20px" }}>
            {!shouldHideVisibilityForFile(file) &&
              fileType !== "Cover Sheet" &&
              fileType !== "Perturbations Test Report" &&
              hasFileVisibilityPermission && (
                <Tooltip title={getVisibilityTooltip(file)}>
                  <IconButton disableRipple onClick={() => onVisibilityClick(file)}>
                    {hasVisibilityGrants(file) ? (
                      <VisibilityIcon sx={{ color: "primary.main" }} />
                    ) : (
                      <VisibilityOffIcon />
                    )}
                  </IconButton>
                </Tooltip>
              )}

            <IconButton
              disableRipple
              onClick={() => {
                setEditFileName(file);
                setOriginalFileName(file);
              }}
            >
              <EditIcon />
            </IconButton>
            {/* Only show remove button for new files OR if platform operator */}
            {canRemoveFile(file) && (
              <IconButton disableRipple onClick={removeFile(file.id, file.certificateId)}>
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const CertificateFileUploadMulti: React.FC<Props> = (props) => {
  const { fileType, className, formAtom, formVersion, isEdit, testId } = props;

  // Determine which state management to use based on formVersion
  const isGridForming = formVersion === "matrixCertificateCreate";
  const uploadCertificateFilesMultiSelectorFamily = isGridForming
    ? gridFormingUploadCertificateFilesMultiSelectorFamily
    : standardUploadCertificateFilesMultiSelectorFamily;
  const getFileTypeIdForFileTypeName = isGridForming ? gridFormingGetFileTypeId : standardGetFileTypeId;

  const userInfo = useGetUserInfo();
  const isPlatformOperator = userInfo?.role === "PLATFORM_OPERATOR";

  const [editFileName, setEditFileName] = useState<UploadCertificateFile | undefined>(undefined);
  const [filesToUpload, setFilesToUpload] = useRecoilState(uploadCertificateFilesMultiSelectorFamily(fileType));
  const [originalFileName, setOriginalFileName] = useState<UploadCertificateFile | undefined>(undefined);
  const [visibilityFile, setVisibilityFile] = useState<UploadCertificateFile | undefined>(undefined);
  const [openVisibilityModal, setOpenVisibilityModal] = useState(false);
  const setFormState = useSetRecoilState(formAtom);

  const setFileName = (fileName: string) => {
    setEditFileName((file) => {
      if (!file) return;
      return { ...file, certificateFileName: fileName };
    });
  };
  const stopEdit = () => {
    setEditFileName(undefined);
  };

  const onConfirm = () => {
    setFormState((updateModifiedByUser) => {
      return { ...updateModifiedByUser, __modifiedByUser: 2 };
    });

    setFilesToUpload((files) => {
      if (!editFileName) return files;
      if (editFileName.id === undefined) {
        return files.map((file) => (file.certificateId === editFileName.certificateId ? editFileName : file));
      }
      return files.map((file) => (file.id === editFileName.id ? editFileName : file));
    });
    setEditFileName(undefined);
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const converts = acceptedFiles.map<Promise<UploadCertificateFile>>(async (file) => {
        const certificateFileName = file.name;
        const certificateFileTypeId = getFileTypeIdForFileTypeName(fileType);
        const certificateId = `${dummyIdPrefix}${uuidv4()}`;
        const base64FileString = await toBase64([file]);
        return { certificateFileName, certificateFileTypeId, certificateId, base64FileString, size: file.size };
      });
      const filesToAdd = await Promise.all(converts);
      setFilesToUpload((files) => [...(files ?? []), ...filesToAdd]);
    },
    [fileType, setFilesToUpload, getFileTypeIdForFileTypeName]
  );

  const removeFile = (fileId: string | undefined, dummyCerificateId: string) => () => {
    if (fileId) {
      setFilesToUpload((files) => (files ?? []).filter((file) => file.id !== fileId));
    } else {
      setFilesToUpload((files) => (files ?? []).filter((file) => file.certificateId !== dummyCerificateId));
    }
  };

  const handleVisibilityClick = (file: UploadCertificateFile) => {
    setVisibilityFile(file);
    setOpenVisibilityModal(true);
  };

  const handleVisibilitySave = (file: UploadCertificateFile, roles: TenantRole[], tenantIds: string[]) => {
    setFormState((updateModifiedByUser) => {
      return { ...updateModifiedByUser, __modifiedByUser: 2 };
    });

    setFilesToUpload((files) => {
      // Use empty arrays to clear visibility - backend expects [] to clear, not null/undefined
      const updatedFile = {
        ...file,
        grantAccessToRoles: roles,
        grantAccessToTenantIds: tenantIds,
      };
      if (file.id === undefined) {
        return (files ?? []).map((f) => (f.certificateId === file.certificateId ? updatedFile : f));
      }
      return (files ?? []).map((f) => (f.id === file.id ? updatedFile : f));
    });
  };

  const { open, getInputProps } = useDropzone({
    onDrop,
    noDrag: true,
    accept: props.mimeTypes.reduce((acc, mimeType) => ({ ...acc, [mimeType]: [] }), {}),
    multiple: true,
    useFsAccessApi: false,
  });
  return (
    <Box className={className}>
      <EditFileNameModal
        fileName={editFileName?.certificateFileName}
        originalFileName={originalFileName?.certificateFileName ?? ""}
        setFileName={setFileName}
        onConfirm={onConfirm}
        onClose={stopEdit}
        testId={`edit-file-name-modal-${testId}`}
      />
      <FileVisibilityModal
        open={openVisibilityModal}
        file={visibilityFile}
        onClose={() => {
          setOpenVisibilityModal(false);
          setVisibilityFile(undefined);
        }}
        onSave={handleVisibilitySave}
      />
      <FilesList
        files={filesToUpload ?? []}
        removeFile={removeFile}
        setEditFileName={setEditFileName}
        setOriginalFileName={setOriginalFileName}
        isEdit={isEdit}
        fileType={fileType}
        onVisibilityClick={handleVisibilityClick}
        isPlatformOperator={isPlatformOperator}
      />
      <StyledUploadButton variant="outlined" onClick={open} startIcon={<AttachFileIcon />}>
        <input {...getInputProps()} />
        {props.title ?? translations.globals.button.uploadGridConnectionValues}
      </StyledUploadButton>
    </Box>
  );
};
export default CertificateFileUploadMulti;
